import * as THREE from 'three';
import { type FC, useCallback, useMemo, useRef } from 'react';
import type { Number3 } from '../types';
import { DraggableCube, type DraggableCubeProps } from './DraggableCube';
import { WireframeBox } from './WireframeBox';
import { useAbyssViewerContext } from './AbyssViewerContext';
import {
  defaultVisibilityBoxStyle,
  type Transform,
  type VisibilityBoxControlStyle,
} from './VisibilityBox';
import { type DraggingInfo } from './DraggingHelper';

export interface VisibilityBoxControlProps {
  box: Transform;
  setBox: (box: Transform) => void;
  step: [x: number, y: number, z: number];
  style: VisibilityBoxControlStyle;
  showWireframe?: boolean;
}

export const VisibilityBoxControl: FC<VisibilityBoxControlProps> = ({
  box,
  setBox,
  step,
  style = defaultVisibilityBoxStyle,
  showWireframe = true,
}) => {
  const { draggingHelper } = useAbyssViewerContext();

  const { position: center, rotation, scale } = box;

  const localGroupReference = useRef<THREE.Group | null>(null);

  const getLocalGroup = useCallback(() => localGroupReference.current ?? undefined, []);

  const draggingOrigin = useRef<{ center: Number3; scale: Number3 }>();

  const onStartDragging = useCallback(() => {
    draggingOrigin.current = { center, scale };
  }, [center, scale]);

  const onStopDragging = useCallback(() => {
    draggingOrigin.current = undefined;
  }, []);

  const draggingBoxStyle = style.draggingBox ?? defaultVisibilityBoxStyle.draggingBox;
  const boxFrameStyle = style.boxFrame ?? defaultVisibilityBoxStyle.boxFrame;

  const draggableCubes = useMemo(() => {
    if (!draggingHelper) return undefined;

    const draggingBoxSize = (scale[0] + scale[1] + scale[2]) * 0.015;
    const draggingBoxMargin = 6 * draggingBoxSize;

    const cubes: DraggableCubeProps[] = [];

    for (let index = 0; index < 3; index += 1) {
      for (let sign = -1; sign < 2; sign += 2) {
        const draggingDirection: Number3 = [0, 0, 0];
        draggingDirection[index] = sign;

        const position: Number3 = [0, 0, 0];
        position[index] += sign * (0.5 * scale[index] + draggingBoxMargin);

        const onDragging = (info: DraggingInfo) => {
          const localGroup = localGroupReference.current;
          if (!localGroup) return;
          if (!draggingOrigin.current) return;

          const { groupWorldMatrix } = info;

          const offset = info.position[index] - info.startPosition[index];

          const snapToGrid = (coord: number, gridStep: number) => {
            return gridStep === 0 ? coord : Math.round(coord / gridStep) * gridStep;
          };

          const coordStep = step[index];

          const coordScale = draggingOrigin.current.scale[index];

          const newCoordScale = snapToGrid(scale[index] + sign * offset, coordStep);

          const scaleStep = newCoordScale - coordScale;
          const centerStep = scaleStep * 0.5;

          const originCenterVec = new THREE.Vector3(...draggingOrigin.current.center);
          const newCenterVec = new THREE.Vector3(...draggingDirection);
          newCenterVec
            .normalize()
            .transformDirection(groupWorldMatrix)
            .multiplyScalar(centerStep)
            .add(originCenterVec);

          const newScale: Number3 = [...draggingOrigin.current.scale];
          newScale[index] = Math.max(0, newCoordScale);

          const newBox: Transform = { position: newCenterVec.toArray(), rotation, scale: newScale };
          setBox(newBox);
        };
        cubes.push({
          draggingHelper,
          position,
          draggingDirection,
          draggingStep: step,
          onDragging,
          onStartDragging,
          onStopDragging,
          color: draggingBoxStyle.color,
          draggingColor: draggingBoxStyle.draggingColor,
          size: draggingBoxStyle.size,
          getLocalGroup,
        });
      }
    }
    return cubes;
  }, [
    draggingHelper,
    setBox,
    rotation,
    scale,
    step,
    draggingBoxStyle,
    getLocalGroup,
    onStartDragging,
    onStopDragging,
  ]);

  const draggableCubesComp = useMemo(
    () =>
      draggableCubes?.map((draggableCubeProps, index) => (
        <DraggableCube key={index} {...draggableCubeProps} />
      )),
    [draggableCubes]
  );

  return (
    <group ref={localGroupReference} position={center} rotation={rotation}>
      {showWireframe && (
        <WireframeBox
          color={boxFrameStyle.color}
          position={undefined}
          rotation={undefined}
          scale={scale}
          lineWidth={boxFrameStyle.lineWidth}
        />
      )}
      {draggableCubesComp}
    </group>
  );
};
